import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Download,
  Building2,
  Plus,
  Search,
  Trash2,
  Upload,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  ContextHero,
  KpiCard,
  MoneyAmount,
  EmptyStateModern,
  PageContainer,
  SectionTitle,
} from '@/components/design-system';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useAllActivites } from '@/hooks/queries/useActivites';
import {
  useDettesFournisseurs,
  useDeleteDette,
  type DetteFournisseurRow,
  type DetteFournisseurStatut,
} from '@/hooks/queries/useDettesFournisseurs';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { ImportDettesFournisseursDialog } from '@/components/imports/ImportDettesFournisseursDialog';
import { CreateDetteFournisseurDialog } from '@/components/forms/CreateDetteFournisseurDialog';
import { EnregistrerPaiementDetteDialog } from '@/components/forms/EnregistrerPaiementDetteDialog';
import { MarquerDettesPayeesEnLotDialog } from '@/components/forms/MarquerDettesPayeesEnLotDialog';
import { DetteFournisseurDrawer } from '@/components/finances/DetteFournisseurDrawer';
import { StatutPaiementBadge, isPaiementEnRetard } from '@/components/finances/StatutPaiementBadge';
import { cn } from '@/lib/utils';
import { usePermission } from '@/hooks/usePermission';
import { AccessDenied } from '@/components/AccessDenied';

const STATUT_LABEL: Record<DetteFournisseurStatut, string> = {
  a_payer: 'À payer',
  partiel: 'Partiel',
  paye: 'Payé',
};

const STATUT_FILTERS: { value: DetteFournisseurStatut | ''; label: string }[] = [
  { value: '', label: 'Tous' },
  { value: 'a_payer', label: 'À payer' },
  { value: 'partiel', label: 'Partiels' },
  { value: 'paye', label: 'Payés' },
];

export function DettesFournisseursPage(): JSX.Element {
  const canView = usePermission('dettes_fournisseurs.view');
  const canEdit = usePermission('dettes_fournisseurs.edit');
  const { data: entreprises = [] } = useEntreprises();
  const { data: activites = [] } = useAllActivites();

  const eurl =
    entreprises.find((e) => /kost/i.test(e.nom ?? '') && /eurl/i.test(e.nom ?? '')) ??
    entreprises[0] ??
    null;
  const [entrepriseId, setEntrepriseId] = useState<string>('');
  const effectiveEntrepriseId = entrepriseId || eurl?.id || '';
  const activiteB2B = useMemo(
    () =>
      activites.find(
        (a) => a.entreprise_id === effectiveEntrepriseId && /b2b|formation b2b/i.test(a.nom ?? ''),
      ),
    [activites, effectiveEntrepriseId],
  );

  const [annee, setAnnee] = useState<number | ''>('');
  const [statut, setStatut] = useState<DetteFournisseurStatut | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 300);
  const [importOpen, setImportOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editDette, setEditDette] = useState<DetteFournisseurRow | null>(null);
  const [enregistrerPaiementDette, setEnregistrerPaiementDette] =
    useState<DetteFournisseurRow | null>(null);
  const [bulkPayOpen, setBulkPayOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<DetteFournisseurRow | null>(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [drawerDette, setDrawerDette] = useState<DetteFournisseurRow | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data: dettes = [], isLoading } = useDettesFournisseurs({
    entrepriseId: effectiveEntrepriseId || null,
    annee: annee === '' ? null : annee,
    statut: statut || null,
    search,
  });
  const deleteDette = useDeleteDette();

  const yearsAvailable = useMemo(() => {
    const set = new Set<number>();
    const current = new Date().getFullYear();
    for (let y = 2020; y <= current + 1; y++) set.add(y);
    for (const d of dettes) set.add(d.annee);
    return Array.from(set).sort((a, b) => b - a);
  }, [dettes]);

  const selectedDettes = useMemo(
    () => dettes.filter((d) => selectedIds.has(d.id)),
    [dettes, selectedIds],
  );

  const kpis = useMemo(() => {
    const enCours = dettes
      .filter((d) => d.statut !== 'paye')
      .reduce((a, d) => a + (d.montant_dette - d.montant_regle), 0);
    const currentYear = new Date().getFullYear();
    // Sprint 6.9.10 — fallback updated_at si date_virement non renseignée (cas imports historiques)
    const regleYTD = dettes
      .filter((d) => {
        if (d.montant_regle <= 0) return false;
        const ref = d.date_virement ?? d.updated_at;
        if (!ref) return false;
        return new Date(ref).getFullYear() === currentYear;
      })
      .reduce((a, d) => a + d.montant_regle, 0);
    const enRetard = dettes.filter((d) => isPaiementEnRetard(d.statut, d.date_formation)).length;
    const byFournisseur = new Map<string, number>();
    for (const d of dettes) {
      if (d.statut === 'paye') continue;
      const k = d.fournisseur_nom;
      byFournisseur.set(k, (byFournisseur.get(k) ?? 0) + (d.montant_dette - d.montant_regle));
    }
    let topFournisseur: { nom: string; total: number } | null = null;
    for (const [nom, total] of byFournisseur) {
      if (!topFournisseur || total > topFournisseur.total) topFournisseur = { nom, total };
    }
    return { enCours, regleYTD, enRetard, topFournisseur };
  }, [dettes]);

  const toggleSelect = (id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = (): void => {
    if (selectedIds.size === dettes.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(dettes.map((d) => d.id)));
  };

  const exportSelectionCSV = (): void => {
    const rows = dettes.filter((d) => selectedIds.has(d.id));
    if (rows.length === 0) {
      toast.warning('Aucune ligne sélectionnée');
      return;
    }
    const headers = [
      'Fournisseur',
      'Téléphone',
      'RIB',
      'Email',
      'Année',
      'Formation',
      'Date',
      'N° facture',
      'Mt dette',
      'Mt réglé',
      'Statut',
      'Mode',
      'Réf',
      'Date virement',
      'Observation',
    ];
    const csvLines = rows.map((d) =>
      [
        d.fournisseur_nom,
        d.fournisseur_telephone ?? '',
        d.fournisseur_rib ?? '',
        d.fournisseur_email ?? '',
        d.annee,
        d.formation_titre ?? '',
        d.date_formation ?? '',
        d.reference_facture ?? '',
        d.montant_dette,
        d.montant_regle,
        STATUT_LABEL[d.statut],
        d.mode_paiement ?? '',
        d.reference_operation ?? '',
        d.date_virement ?? '',
        (d.observation ?? '').replace(/"/g, '""'),
      ]
        .map((v) => {
          const s = String(v ?? '');
          return /[",\n;]/.test(s) ? `"${s}"` : s;
        })
        .join(','),
    );
    const csv = [headers.join(','), ...csvLines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dettes_fournisseurs_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${rows.length} ligne(s) exportée(s)`);
  };

  if (!canView) return <AccessDenied feature="dettes_fournisseurs.view" />;

  return (
    <PageContainer>
      <ContextHero
        variant="finances"
        icon={<Building2 className="h-5 w-5" />}
        title="Dettes Fournisseurs"
        subtitle="Hôtels, traiteurs, services — import Excel + cross-références automatiques vers la Caisse"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={effectiveEntrepriseId}
              onChange={(e) => setEntrepriseId(e.target.value)}
              className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
              aria-label="Entreprise"
            >
              {entreprises.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                </option>
              ))}
            </select>
            {canEdit && (
              <>
                <button
                  type="button"
                  onClick={() => setImportOpen(true)}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <Upload className="h-3.5 w-3.5" /> Importer Excel
                </button>
                <button
                  type="button"
                  onClick={() => setCreateOpen(true)}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md bg-orange-600 px-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-orange-700"
                >
                  <Plus className="h-3.5 w-3.5" /> Nouvelle dette
                </button>
              </>
            )}
          </div>
        }
      />

      {/* KPIs */}
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Total en cours"
          value={<MoneyAmount amount={kpis.enCours} variant="kpi" />}
          variant="finances"
          loading={isLoading}
          hint="Reste à payer (a_payer + partiel)"
        />
        <KpiCard
          label="Réglé YTD"
          value={<MoneyAmount amount={kpis.regleYTD} variant="kpi" />}
          variant="echeances"
          icon={<CheckCircle2 className="h-4 w-4" />}
          loading={isLoading}
        />
        <KpiCard
          label="En retard >30j"
          value={<span className="text-3xl font-black tabular-nums">{kpis.enRetard}</span>}
          unit={kpis.enRetard > 1 ? 'dettes' : 'dette'}
          variant="dashboard"
          loading={isLoading}
        />
        <KpiCard
          label="Top fournisseur dette"
          value={
            <span className="text-base font-bold text-slate-900">
              {kpis.topFournisseur?.nom ?? '—'}
            </span>
          }
          hint={
            kpis.topFournisseur
              ? `${kpis.topFournisseur.total.toLocaleString('fr-DZ')} DA`
              : undefined
          }
          variant="finances"
          loading={isLoading}
        />
      </div>

      {/* Filtres */}
      <div className="mt-5 flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 bg-white p-3">
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Année</label>
          <select
            value={annee === '' ? '' : annee}
            onChange={(e) => setAnnee(e.target.value === '' ? '' : Number(e.target.value))}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
          >
            <option value="">— Toutes —</option>
            {yearsAvailable.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Statut</label>
          <div className="flex gap-1">
            {STATUT_FILTERS.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => setStatut(s.value)}
                className={cn(
                  'h-9 rounded-md border px-3 text-xs font-semibold transition-colors',
                  statut === s.value
                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                )}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div className="ml-auto space-y-1">
          <label className="text-xs text-slate-500">Recherche</label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Fournisseur, formation, n° facture…"
              className="h-9 w-64 pl-7"
            />
          </div>
        </div>
        {(annee !== '' || statut || searchInput) && (
          <button
            type="button"
            onClick={() => {
              setAnnee('');
              setStatut('');
              setSearchInput('');
            }}
            className="inline-flex h-9 items-center gap-1 rounded-md px-3 text-xs text-slate-500 hover:bg-slate-100"
          >
            <XCircle className="h-3 w-3" /> Effacer
          </button>
        )}
      </div>

      {/* Sticky bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="sticky top-2 z-20 mt-5 flex flex-wrap items-center gap-2 rounded-xl border border-orange-300 bg-orange-50/95 px-4 py-2.5 shadow-md backdrop-blur">
          <span className="text-sm font-bold text-orange-900">
            {selectedIds.size} dette{selectedIds.size > 1 ? 's' : ''} sélectionnée
            {selectedIds.size > 1 ? 's' : ''}
          </span>
          <span className="text-xs text-orange-700">
            ({selectedDettes.filter((d) => d.statut !== 'paye').length} payable
            {selectedDettes.filter((d) => d.statut !== 'paye').length > 1 ? 's' : ''})
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkPayOpen(true)}
              disabled={selectedDettes.filter((d) => d.statut !== 'paye').length === 0}
              className={cn(
                'inline-flex h-8 items-center gap-1.5 rounded-md bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-700',
                selectedDettes.filter((d) => d.statut !== 'paye').length === 0 &&
                  'cursor-not-allowed opacity-50',
              )}
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Solder en lot
            </button>
            <button
              type="button"
              onClick={exportSelectionCSV}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Download className="h-3.5 w-3.5" /> Exporter CSV
            </button>
            <button
              type="button"
              onClick={() => setConfirmBulkDelete(true)}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-rose-600 px-3 text-xs font-semibold text-white hover:bg-rose-700"
            >
              <Trash2 className="h-3.5 w-3.5" /> Supprimer
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs text-slate-500 hover:bg-slate-100"
            >
              <XCircle className="h-3 w-3" /> Désélectionner
            </button>
          </div>
        </div>
      )}

      {/* Tableau */}
      <div className="mt-5">
        <SectionTitle
          trailing={
            <span className="text-xs text-slate-500">
              {dettes.length} dette{dettes.length > 1 ? 's' : ''}
            </span>
          }
        >
          Dettes
        </SectionTitle>
        <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white">
          {isLoading ? (
            <div className="space-y-1 p-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-9 animate-pulse rounded-md bg-slate-100" />
              ))}
            </div>
          ) : dettes.length === 0 ? (
            <div className="p-6">
              <EmptyStateModern
                variant="finances"
                icon={<Building2 className="h-6 w-6" />}
                title="Aucune dette fournisseur"
                description="Importez un Excel ou créez une dette manuellement pour démarrer."
                ctaLabel="Importer Excel"
                onCtaClick={() => setImportOpen(true)}
              />
            </div>
          ) : (
            <div className="scrollbar-thin overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/80 text-[10px] font-bold uppercase tracking-widest text-slate-600 backdrop-blur">
                  <tr>
                    <th className="px-2 py-2 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === dettes.length && dettes.length > 0}
                        onChange={toggleAll}
                        aria-label="Tout sélectionner"
                      />
                    </th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Fournisseur</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Année</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">N° facture</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Date</th>
                    <th className="whitespace-nowrap px-2 py-2 text-right">Mt dette</th>
                    <th className="whitespace-nowrap px-2 py-2 text-right">Mt réglé</th>
                    <th className="whitespace-nowrap px-2 py-2 text-right">Reste</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Statut</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Mode</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Date virement</th>
                    <th className="px-2 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {dettes.map((d) => {
                    const enRetard = isPaiementEnRetard(d.statut, d.date_formation);
                    const reste = d.montant_dette - d.montant_regle;
                    const rowTone = enRetard
                      ? 'bg-rose-50/40 border-l-2 border-l-rose-300'
                      : d.statut === 'paye'
                        ? 'bg-emerald-50/30'
                        : d.statut === 'partiel'
                          ? 'bg-amber-50/30'
                          : 'bg-white';
                    return (
                      <tr
                        key={d.id}
                        className={cn(
                          'border-b border-slate-100 transition-colors hover:bg-slate-50',
                          rowTone,
                        )}
                      >
                        <td className="px-2 py-1.5">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(d.id)}
                            onChange={() => toggleSelect(d.id)}
                            aria-label={`Sélectionner ${d.fournisseur_nom}`}
                          />
                        </td>
                        <td
                          className="cursor-pointer px-2 py-1.5 font-semibold text-slate-800 hover:underline"
                          onClick={() => setDrawerDette(d)}
                        >
                          {d.fournisseur_nom}
                        </td>
                        <td className="px-2 py-1.5 tabular-nums">{d.annee}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 font-mono text-[10px] text-slate-600">
                          {d.reference_facture ?? '—'}
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-slate-600">
                          {d.date_formation ?? '—'}
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-right">
                          <MoneyAmount amount={d.montant_dette} />
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-right">
                          {d.montant_regle > 0 ? (
                            <MoneyAmount amount={d.montant_regle} />
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-right font-semibold">
                          {reste > 0 ? (
                            <MoneyAmount amount={reste} />
                          ) : (
                            <span className="text-emerald-600">—</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          <StatutPaiementBadge statut={d.statut} enRetard={enRetard} size="sm" />
                        </td>
                        <td className="px-2 py-1.5 text-slate-600">{d.mode_paiement ?? '—'}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-slate-600">
                          {d.date_virement ?? '—'}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          {d.statut !== 'paye' ? (
                            <button
                              type="button"
                              onClick={() => setEnregistrerPaiementDette(d)}
                              className="inline-flex h-7 items-center gap-1 rounded-md bg-emerald-600 px-2 text-xs font-semibold text-white hover:bg-emerald-700"
                            >
                              <CheckCircle2 className="h-3 w-3" /> Régler
                            </button>
                          ) : d.operation_id ? (
                            <Badge variant="outline" className="text-[10px]">
                              Op liée
                            </Badge>
                          ) : (
                            <span className="text-[10px] text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <ImportDettesFournisseursDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        entrepriseId={effectiveEntrepriseId}
        activiteId={activiteB2B?.id ?? null}
      />
      <CreateDetteFournisseurDialog
        open={createOpen || editDette !== null}
        onOpenChange={(o) => {
          if (!o) {
            setCreateOpen(false);
            setEditDette(null);
          }
        }}
        entrepriseId={effectiveEntrepriseId}
        activiteId={activiteB2B?.id ?? null}
        dette={editDette}
      />
      <EnregistrerPaiementDetteDialog
        open={!!enregistrerPaiementDette}
        onOpenChange={(o) => !o && setEnregistrerPaiementDette(null)}
        dette={enregistrerPaiementDette}
      />
      <MarquerDettesPayeesEnLotDialog
        open={bulkPayOpen}
        onOpenChange={setBulkPayOpen}
        dettes={selectedDettes}
        entrepriseId={effectiveEntrepriseId}
        onDone={() => setSelectedIds(new Set())}
      />
      <DetteFournisseurDrawer
        dette={drawerDette}
        onClose={() => setDrawerDette(null)}
        onEnregistrerPaiement={(d) => {
          setEnregistrerPaiementDette(d);
          setDrawerDette(null);
        }}
        onEdit={(d) => {
          setEditDette(d);
          setDrawerDette(null);
        }}
        onDelete={(d) => {
          setConfirmDelete(d);
          setDrawerDette(null);
        }}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        destructive
        title={
          confirmDelete ? `Supprimer la dette de ${confirmDelete.fournisseur_nom} ?` : 'Supprimer ?'
        }
        description={
          confirmDelete?.operation_id
            ? "Cette action est irréversible. L'opération Caisse liée sera également supprimée."
            : 'Cette action est irréversible.'
        }
        loading={deleteDette.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return;
          try {
            await deleteDette.mutateAsync({
              id: confirmDelete.id,
              operation_id: confirmDelete.operation_id,
            });
            toast.success('Dette supprimée');
            setConfirmDelete(null);
          } catch (e) {
            toast.error(`Échec : ${(e as Error).message}`);
          }
        }}
      />
      <ConfirmDialog
        open={confirmBulkDelete}
        onOpenChange={setConfirmBulkDelete}
        destructive
        title={`Supprimer ${selectedIds.size} dette(s) ?`}
        description="Action irréversible. Les opérations Caisse liées seront aussi supprimées si présentes."
        loading={deleteDette.isPending}
        onConfirm={async () => {
          let ok = 0;
          const errs: string[] = [];
          for (const d of selectedDettes) {
            try {
              await deleteDette.mutateAsync({ id: d.id, operation_id: d.operation_id });
              ok++;
            } catch (e) {
              errs.push((e as Error).message);
            }
          }
          if (errs.length === 0) toast.success(`${ok} dette(s) supprimée(s)`);
          else toast.warning(`${ok} OK · ${errs.length} échec(s)`);
          setConfirmBulkDelete(false);
          setSelectedIds(new Set());
        }}
      />
    </PageContainer>
  );
}
